import { Button } from "@/components/ui/button";
import { Check, X, Phone, Mail, Globe, MapPin, Linkedin, Twitter, Sparkles, Crown, Eye, Heart, Star, Zap, Award } from "lucide-react";

const HomepageUnforgettable = () => {
  const features = [
    { icon: Eye, title: "Your Photo Front & Center", desc: "They see your face, not just text" },
    { icon: Crown, title: "Professional Bio", desc: "Your story, your way" },
    { icon: Sparkles, title: "Beautiful Design", desc: "Stand out from the crowd" },
    { icon: Heart, title: "Instant Save", desc: "One tap to their contacts" },
  ];

  const testimonials = [
    { name: "Alexandra Wright", role: "Marketing Executive", quote: "People remember me now. QCard turned my introductions into lasting impressions." },
    { name: "David Kim", role: "Financial Advisor", quote: "My clients always say how professional my QCard looks. It sets the tone for our relationship." },
    { name: "Rachel Martinez", role: "Event Planner", quote: "I stand out at every networking event. QCard is my secret weapon." },
  ];

  const comparisons = [
    { feature: "Photo & Visual Identity", paper: false, qcard: true },
    { feature: "Professional Bio", paper: false, qcard: true },
    { feature: "Instant Contact Save", paper: false, qcard: true },
    { feature: "Social Media Links", paper: false, qcard: true },
    { feature: "Always Updated", paper: false, qcard: true },
    { feature: "Memorable Experience", paper: false, qcard: true },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#0f0f1a] via-[#1a1025] to-[#0f0f1a] text-white">
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center overflow-hidden">
        {/* Elegant gradient orbs */}
        <div className="absolute top-0 left-1/4 w-[500px] h-[500px] bg-purple-600/20 rounded-full blur-[150px]" />
        <div className="absolute bottom-0 right-1/4 w-[400px] h-[400px] bg-pink-500/20 rounded-full blur-[120px]" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-violet-500/10 rounded-full blur-[100px]" />
        
        <div className="container mx-auto px-4 py-20 relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-8">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-purple-500/20 to-pink-500/20 border border-purple-500/30 rounded-full">
                <Sparkles className="w-4 h-4 text-purple-400" />
                <span className="text-sm bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent font-medium">Be Unforgettable</span>
              </div>
              
              <h1 className="text-5xl md:text-7xl font-bold leading-tight">
                <span className="bg-gradient-to-r from-purple-400 via-pink-400 to-orange-400 bg-clip-text text-transparent">Stand out.</span>
                <span className="block mt-2">Stay remembered.</span>
                <span className="block mt-2 text-gray-400">Get saved.</span>
              </h1>
              
              <p className="text-xl text-gray-400 max-w-lg">
                Without a memorable profile or a spot in someone's Contacts app, you blend into the crowd. QCard changes everything.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4">
                <Button size="lg" className="bg-gradient-to-r from-purple-600 via-pink-500 to-orange-500 hover:opacity-90 text-white px-8 py-6 text-lg rounded-full shadow-lg shadow-purple-500/25">
                  <Sparkles className="w-5 h-5 mr-2" />
                  Create Your QCard
                </Button>
                <Button size="lg" variant="outline" className="border-purple-500/30 text-white hover:bg-purple-500/10 px-8 py-6 text-lg rounded-full">
                  See Examples
                </Button>
              </div>
            </div>
            
            {/* Premium Phone Mockup */}
            <div className="relative flex justify-center">
              <div className="relative">
                {/* Glow ring */}
                <div className="absolute inset-0 bg-gradient-to-r from-purple-500 via-pink-500 to-orange-500 rounded-[3.5rem] blur-xl opacity-50 scale-105" />
                
                {/* Phone */}
                <div className="relative w-80 h-[620px] bg-gradient-to-b from-gray-900 to-black rounded-[3rem] border-2 border-purple-500/30 shadow-2xl">
                  <div className="absolute top-4 left-1/2 -translate-x-1/2 w-28 h-7 bg-black rounded-full" />
                  <div className="absolute inset-3 top-14 bg-gradient-to-b from-[#1a1025] to-[#0f0f1a] rounded-[2.5rem] overflow-hidden">
                    {/* Profile Card */}
                    <div className="p-6 space-y-6">
                      {/* Header gradient */}
                      <div className="h-28 -mx-6 -mt-6 bg-gradient-to-br from-purple-600 via-pink-500 to-orange-500" />
                      
                      {/* Avatar */}
                      <div className="relative -mt-20 text-center">
                        <div className="w-28 h-28 mx-auto rounded-full bg-gradient-to-br from-purple-400 to-pink-500 p-1">
                          <div className="w-full h-full rounded-full bg-gray-800 flex items-center justify-center text-4xl font-bold">
                            SM
                          </div>
                        </div>
                        <div className="mt-4">
                          <h3 className="text-2xl font-bold">Sarah Mitchell</h3>
                          <p className="text-purple-400 font-medium">Creative Director</p>
                          <p className="text-gray-500 text-sm">Design Studio Co.</p>
                        </div>
                      </div>
                      
                      {/* Bio */}
                      <p className="text-center text-gray-400 text-sm px-4">
                        Crafting visual stories that connect brands with their audience. 10+ years of creative excellence.
                      </p>
                      
                      {/* CTA */}
                      <Button className="w-full bg-gradient-to-r from-purple-600 to-pink-500 hover:opacity-90 rounded-xl py-5">
                        Add to Contacts
                      </Button>
                      
                      {/* Social icons */}
                      <div className="flex justify-center gap-4">
                        <div className="w-10 h-10 rounded-full bg-purple-500/20 flex items-center justify-center">
                          <Linkedin className="w-5 h-5 text-purple-400" />
                        </div>
                        <div className="w-10 h-10 rounded-full bg-pink-500/20 flex items-center justify-center">
                          <Twitter className="w-5 h-5 text-pink-400" />
                        </div>
                        <div className="w-10 h-10 rounded-full bg-orange-500/20 flex items-center justify-center">
                          <Globe className="w-5 h-5 text-orange-400" />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Problem Section */}
      <section className="py-24 relative">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-bold mb-6">The Problem with Being <span className="text-gray-500">Forgettable</span></h2>
            </div>
            
            <div className="grid md:grid-cols-2 gap-8">
              <div className="p-8 bg-gradient-to-b from-gray-800/30 to-gray-900/30 border border-gray-800 rounded-3xl">
                <h3 className="text-2xl font-bold mb-4 text-gray-400">What Happens Now</h3>
                <ul className="space-y-4 text-gray-400">
                  <li className="flex items-start gap-3">
                    <X className="w-6 h-6 text-red-500 flex-shrink-0 mt-0.5" />
                    <span>You invest in networking but leave no lasting impression</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <X className="w-6 h-6 text-red-500 flex-shrink-0 mt-0.5" />
                    <span>Clients can't find you when they need you</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <X className="w-6 h-6 text-red-500 flex-shrink-0 mt-0.5" />
                    <span>Your brand becomes forgettable the moment you walk away</span>
                  </li>
                </ul>
              </div>
              
              <div className="p-8 bg-gradient-to-b from-purple-900/20 to-pink-900/20 border border-purple-500/30 rounded-3xl">
                <h3 className="text-2xl font-bold mb-4 bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent">With QCard</h3>
                <ul className="space-y-4">
                  <li className="flex items-start gap-3">
                    <Check className="w-6 h-6 text-green-500 flex-shrink-0 mt-0.5" />
                    <span>Beautiful digital identity that lives in their phone</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <Check className="w-6 h-6 text-green-500 flex-shrink-0 mt-0.5" />
                    <span>Your photo, story, and brand — instantly saved</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <Check className="w-6 h-6 text-green-500 flex-shrink-0 mt-0.5" />
                    <span>A first impression that doesn't fade</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-24">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              What Makes You
              <span className="bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent"> Unforgettable</span>
            </h2>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, index) => (
              <div key={index} className="group p-8 bg-gradient-to-b from-gray-800/30 to-gray-900/30 border border-gray-800 rounded-3xl hover:border-purple-500/50 transition-all hover:scale-105">
                <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-purple-500/20 to-pink-500/20 flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                  <feature.icon className="w-7 h-7 text-purple-400" />
                </div>
                <h3 className="text-xl font-bold mb-2">{feature.title}</h3>
                <p className="text-gray-400">{feature.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-24 bg-gradient-to-b from-transparent via-purple-950/10 to-transparent">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-6">Instant & Effortless</h2>
            <p className="text-xl text-gray-400">One tap adds you to their contacts forever</p>
          </div>
          
          <div className="max-w-4xl mx-auto">
            <div className="grid md:grid-cols-3 gap-8">
              {[
                { step: "1", title: "Share Your Link", desc: "Via text, email, QR, or NFC tap" },
                { step: "2", title: "They See Your Profile", desc: "Beautiful, professional, memorable" },
                { step: "3", title: "Saved to Contacts", desc: "One tap — you're in their phone" },
              ].map((item, index) => (
                <div key={index} className="text-center">
                  <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-gradient-to-br from-purple-600 to-pink-500 flex items-center justify-center text-2xl font-bold">
                    {item.step}
                  </div>
                  <h3 className="text-xl font-bold mb-2">{item.title}</h3>
                  <p className="text-gray-400">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Comparison */}
      <section className="py-24">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-6">Forgettable vs Unforgettable</h2>
          </div>
          
          <div className="max-w-3xl mx-auto">
            <div className="bg-gradient-to-b from-gray-800/30 to-gray-900/30 border border-gray-800 rounded-3xl overflow-hidden">
              <div className="grid grid-cols-3 gap-4 p-6 bg-gradient-to-r from-gray-800/50 to-purple-900/30 font-semibold">
                <div>Experience</div>
                <div className="text-center text-gray-500">Paper Card</div>
                <div className="text-center bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent">QCard</div>
              </div>
              {comparisons.map((item, index) => (
                <div key={index} className="grid grid-cols-3 gap-4 p-6 border-t border-gray-800/50 hover:bg-purple-500/5 transition-colors">
                  <div className="text-gray-300">{item.feature}</div>
                  <div className="text-center">
                    <X className="w-6 h-6 text-gray-600 mx-auto" />
                  </div>
                  <div className="text-center">
                    <Check className="w-6 h-6 text-purple-400 mx-auto" />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Free Section */}
      <section className="py-24 bg-gradient-to-b from-transparent via-purple-950/20 to-transparent">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <Award className="w-16 h-16 mx-auto text-purple-400 mb-6" />
            <h2 className="text-4xl md:text-5xl font-bold mb-6">Premium Experience. Zero Cost.</h2>
            <p className="text-xl text-gray-400 mb-12">
              Your unforgettable professional identity — completely free. No trials, no hidden fees.
            </p>
            
            <div className="inline-flex flex-wrap justify-center gap-4">
              {["Beautiful Profile", "Add to Contacts", "Social Links", "Analytics", "Unlimited Shares"].map((item, index) => (
                <div key={index} className="px-6 py-3 bg-gradient-to-r from-purple-500/10 to-pink-500/10 border border-purple-500/30 rounded-full flex items-center gap-2">
                  <Check className="w-5 h-5 text-purple-400" />
                  <span>{item}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-24">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-6">Professionals Who Stand Out</h2>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <div key={index} className="p-8 bg-gradient-to-b from-gray-800/30 to-gray-900/30 border border-gray-800 rounded-3xl hover:border-purple-500/30 transition-colors">
                <div className="flex gap-1 mb-6">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="w-5 h-5 text-purple-400 fill-purple-400" />
                  ))}
                </div>
                <p className="text-lg text-gray-300 mb-6">"{testimonial.quote}"</p>
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center font-bold">
                    {testimonial.name.split(' ').map(n => n[0]).join('')}
                  </div>
                  <div>
                    <p className="font-semibold">{testimonial.name}</p>
                    <p className="text-sm text-gray-400">{testimonial.role}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-24">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center relative">
            <div className="absolute inset-0 bg-gradient-to-r from-purple-600/20 via-pink-500/20 to-orange-500/20 blur-3xl rounded-full" />
            <div className="relative">
              <h2 className="text-4xl md:text-6xl font-bold mb-6">
                Ready to Be
                <span className="bg-gradient-to-r from-purple-400 via-pink-400 to-orange-400 bg-clip-text text-transparent"> Unforgettable?</span>
              </h2>
              <p className="text-xl text-gray-400 mb-12">
                A professional identity that is unmistakably yours.
              </p>
              <Button size="lg" className="bg-gradient-to-r from-purple-600 via-pink-500 to-orange-500 hover:opacity-90 text-white px-12 py-8 text-xl rounded-full shadow-lg shadow-purple-500/25">
                <Sparkles className="w-6 h-6 mr-2" />
                Get Your Free QCard
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 border-t border-gray-800">
        <div className="container mx-auto px-4 text-center text-gray-400">
          <p>© 2024 QCard. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default HomepageUnforgettable;